/**
 * Live price / NAV / AUM summary for a single fund.
 *
 * Fetches from `GET /api/data/funds/:ticker/summary` (Postgres-backed,
 * Cache-Control: no-store, ~5 min freshness in practice). Populates NAV and
 * AUM from the latest ingested daily-NAV row; market price, change, spread,
 * and premium/discount remain null until a Twelve Data stream is wired.
 */
import { apiFetch } from "@/lib/api-client";
import type { Fund } from "@etfsite/db/types";
import { useQuery } from "@tanstack/react-query";

export type LiveDataSource = "live" | "nav" | "none";

export interface FundLiveData {
  price: number | null;
  nav: number | null;
  aum: number | null;
  change: number | null;
  changePct: number | null;
  premiumDiscountPct: number | null;
  medianSpreadPct: number | null;
  asOfDate: string | null;
  navAsOfDate: string | null;
  holdingsAsOfDate: string | null;
  source: LiveDataSource;
}

interface FundSummaryResponse {
  ticker: string;
  latest_nav: string | null;
  latest_nav_date: string | null;
  latest_aum: string | null;
  latest_holdings_date: string | null;
  holdings_count: number;
  fund_exists?: boolean;
  // Market fields added to /:ticker/summary (contract locked with the API team).
  // Returned on success AND in the Postgres-error catch fallback (as null).
  // NOTE: summary uses `_pct`; the /daily-nav endpoint keeps its verbose
  // `*_percentage` keys — do not unify, each hook maps its own endpoint.
  latest_market_price?: string | null;
  latest_market_price_change_dollars?: string | null;
  latest_market_price_change_pct?: string | null;
  latest_premium_discount_pct?: string | null;
  latest_median_30_day_spread_pct?: string | null;
}

/**
 * Live market overlay carried on the funds-list response (the `Fund.latest*`
 * fields, joined from Postgres `daily_nav`/holdings by ticker). The detail page
 * already has these on its fund record, so they fill the fields the per-ticker
 * `/summary` probe does NOT return — market price, 1-day change, and
 * premium/discount. Every field is optional, so any `Fund`/`FundDetail` is a
 * structural match without having to widen those display types.
 */
export interface LiveMarketOverlay {
  latestNav?: string | null;
  latestMarketPrice?: string | null;
  latestMarketPriceChangePct?: string | null;
  latestPremiumDiscount?: string | null;
  latestNetAssets?: string | null;
  latestAsOfDate?: string | null;
}

function toNum(v: string | null | undefined): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export const EMPTY: FundLiveData = {
  price: null,
  nav: null,
  aum: null,
  change: null,
  changePct: null,
  premiumDiscountPct: null,
  medianSpreadPct: null,
  asOfDate: null,
  navAsOfDate: null,
  holdingsAsOfDate: null,
  source: "none",
};

function parseSummary(r: FundSummaryResponse): FundLiveData {
  const nav = toNum(r.latest_nav);
  return {
    ...EMPTY,
    nav,
    aum: toNum(r.latest_aum),
    price: toNum(r.latest_market_price),
    change: toNum(r.latest_market_price_change_dollars),
    changePct: toNum(r.latest_market_price_change_pct),
    premiumDiscountPct: toNum(r.latest_premium_discount_pct),
    medianSpreadPct: toNum(r.latest_median_30_day_spread_pct),
    // NAV and market price share the daily_nav row date — expose it as both the
    // NAV snapshot date and the generic as-of so price/premium label correctly.
    navAsOfDate: r.latest_nav_date ?? null,
    asOfDate: r.latest_nav_date ?? null,
    holdingsAsOfDate: r.latest_holdings_date ?? null,
    // Keep "nav" (never "live"): there is no intraday Twelve Data stream, so a
    // "live" source would render a false green "Live" pulse in the hero.
    source: nav != null ? "nav" : "none",
  };
}

/**
 * Live price / NAV / AUM / premium-discount for a single fund.
 *
 * Primary source is `/:ticker/summary` (NAV, AUM, market price, change,
 * premium-discount, spread, and the snapshot dates). Passing the fund record
 * (or any `LiveMarketOverlay`) as the second arg merges the funds-list overlay
 * as a FALLBACK — it carries the same daily_nav snapshot, so the page renders
 * correctly even before/if `/summary` hasn't populated a field. Callers that
 * only need the summary can omit the overlay.
 */
export function useFundLiveData(
  ticker: string | null | undefined,
  overlay?: LiveMarketOverlay,
): FundLiveData | null {
  const { data, isError } = useQuery({
    queryKey: ["fund-summary", ticker],
    queryFn: () =>
      apiFetch<FundSummaryResponse>(`/api/data/funds/${ticker?.toUpperCase()}/summary`),
    enabled: Boolean(ticker),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Return null on error so callers can distinguish "no data yet" from "loading".
  // In practice the backend always returns 200 with nulls when ingestion hasn't
  // run yet, so isError only fires on genuine network/server failures.
  if (isError) return null;
  const base = data ? parseSummary(data) : EMPTY;
  if (!overlay) return base;

  // Fill any gap in the summary from the funds-list overlay (same daily_nav
  // snapshot, joined on the list endpoint). Summary wins; overlay is fallback.
  const nav = base.nav ?? toNum(overlay.latestNav);
  const price = base.price ?? toNum(overlay.latestMarketPrice);
  return {
    ...base,
    nav,
    price,
    aum: base.aum ?? toNum(overlay.latestNetAssets),
    changePct: base.changePct ?? toNum(overlay.latestMarketPriceChangePct),
    premiumDiscountPct: base.premiumDiscountPct ?? toNum(overlay.latestPremiumDiscount),
    asOfDate: base.asOfDate ?? overlay.latestAsOfDate ?? null,
    navAsOfDate: base.navAsOfDate ?? overlay.latestAsOfDate ?? null,
    holdingsAsOfDate: base.holdingsAsOfDate ?? overlay.latestAsOfDate ?? null,
    // Never "live" (no intraday stream) — "nav" when we have any live snapshot.
    source: nav != null || price != null ? "nav" : base.source,
  };
}

// ── Formatting helpers (unchanged — kept alongside the hook for co-location) ──

export function formatPrice(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `$${value.toFixed(2)}`;
}

export function formatChange(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(value).toFixed(2)}`;
}

export function formatChangePct(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
}

export function formatSpreadPct(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)}%`;
}

export function formatAum(value: number | null): string {
  if (value == null || !Number.isFinite(value) || value <= 0) return "—";
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 950_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

/**
 * Latest live AUM (total net assets) in dollars from the daily_nav overlay on
 * the funds-list response, or null when the fund has no NAV row. Callers fall
 * back to the static DDB `aum` display string when this returns null.
 */
export function fundLiveAumDollars(fund: Fund): number | null {
  if (fund.latestNetAssets == null) return null;
  const n = Number(fund.latestNetAssets);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Legacy name: previously output "M/D/YYYY" (e.g. "1/5/2025") — now outputs
// "MMM D, YYYY" (e.g. "Jan 5, 2025") to match Dane's live site and the
// fund-detail pages (formatAsOfDate / formatFactAsOf). Rename to formatAsOfLong
// in a future cleanup; keep the name now to avoid rippling to multiple callers.
export function formatAsOfMdy(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const isIso10 = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const candidate = isIso10 ? `${iso}T12:00:00` : iso;
  const d = new Date(candidate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/New_York",
  });
}
